import { ValidationMessage } from "../DependentFields/DependentFields.styles"
import { StyledErrorIcon, TextboxWrapper } from "../FormTextBox/FormTextbox.styles"
import InvoiceHeaderNotification from "../InvoiceHeaderNotification/InvoiceHeaderNotification"
import DependentFields from "../DependentFields/DependentFields"
import FormTextbox from "../FormTextBox/FormTextbox"
import UneditableTextbox from "../UneditableTextbox/UneditableTextbox"
import {
  Header,
  InnerWrapper,
  SidebarWrapper,
  StyledFieldLabel,
  StyledFieldValue,
} from "./InvoiceInformationSidebar.styles"
import SupplierLookup from "../SupplierLookup/SupplierLookup"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  selectEnteredInvoiceInformation,
  selectLGInternalUser,
  updateInvoiceDetail,
} from "../../activeScreenSlice"
import MultilineText from "../MultilineTextbox/MultilineTextbox"
import FailedValidationNotifications from "../FailedValidationNotifications/FailedValidationNotifications"
import { validateCurrency } from "../../util/validateCurrency"
import { filterDuplicates } from "../../util/filterDuplicates"
import { validateFinalDateInput } from "../../util/validateDateInput"
import { validateSpotRatesInput } from "../../util/validateSpotRatesInput"
import Big from "big.js"
import { InvoiceType, ReferenceDataService } from "../../api/generated"
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material"
import BankDetailsLookup from "../BankDetailsLookup/BankDetailsLookup"
import { useEffect, useState, useMemo } from "react"
import { getSupplierDetails } from "../../service/SupplierDetails"
import { lineTaxValidationThresholds } from "../../config/validation"

export default function InvoiceInformationSidebar({
  bypassTaxValidation = false,
}) {
  const invoiceInputs = useAppSelector(selectEnteredInvoiceInformation)
  const dispatch = useAppDispatch()
  const LGInternalUser = useAppSelector(selectLGInternalUser);

  const [userFlags, setUserFlags] = useState([])

  useEffect(() => {
    async function fetchUserFlags() {
      try {
        const flags =
          await ReferenceDataService.getUiInvoiceUserFlagsDataUiInvoiceUserFlagsGet()
        setUserFlags(
          flags.map(({ id, description }) => ({
            key: id,
            label: description,
          })),
        )
      } catch (error) {
        console.error("Error fetching user flags:", error)
        setUserFlags([])
      }
    }
    fetchUserFlags()
  }, [])

  function safeBig(val: string | number | null, fallback = 0) {
    try {
      if (val === null || val === "" || val === "-" || val === "." || val === "-.") {
        return new Big(fallback)
      }
      return new Big(val)
    } catch {
      return new Big(fallback)
    }
  }

  const totalsValidation = useMemo(() => {
    try {
      const castTaxAmount = Big(invoiceInputs.taxAmount || 0)
      const castTotalLineAmount = safeBig(invoiceInputs.totalLineAmount, 0)
      const castGrossAmount = Big(invoiceInputs.grossAmount || 0)
      const taxPlusLineAmounts = castTaxAmount.add(castTotalLineAmount)
      if (!taxPlusLineAmounts.eq(castGrossAmount)) {
        // check that taxAmount plus total line amount equals gross amount
        return "error"
      }
    } catch (err: unknown) {
      return "error"
    }
    return "ok"
  }, [
    invoiceInputs.taxAmount,
    invoiceInputs.grossAmount,
    invoiceInputs.totalLineAmount,
  ])

  function handleUpdateStore(key: string, value: string | number) {
    dispatch(updateInvoiceDetail({ key: key, value: value }))
  }

  const lineTaxTotal = invoiceInputs.lineItems.reduce(
    (acc, item) => acc.add(Big(item.tax_amount || 0)),
    new Big(0),
  )

  const [showSpotRate, setShowSpotRate] = useState(false)

  useEffect(() => {
    async function checkSpotRate() {
      if (invoiceInputs.opcoSlug === "vmuk" && ["USD", "EUR"].includes(invoiceInputs.currency)) {
        try {
          const supplierDetails = await getSupplierDetails(invoiceInputs.supplierId)
          if (supplierDetails.supplier_approval_category.toLowerCase() === "spot rate") {
            setShowSpotRate(true)
          } else {
            setShowSpotRate(false)
          }
        } catch (error) {
          console.error("Error fetching supplier details:", error)
          setShowSpotRate(false)
        }
      } else {
        setShowSpotRate(false)
      }
    }
    checkSpotRate()
  }, [invoiceInputs.opcoSlug, invoiceInputs.currency, invoiceInputs.supplierId])

  const lineTaxValidationThreshold =
    lineTaxValidationThresholds.find(
      (validationConfig) =>
        validationConfig.opcoSlug === invoiceInputs.opcoSlug,
    )?.threshold || new Big(0)
  const taxAmount = new Big(invoiceInputs.taxAmount || 0)

  const validateLineTaxTotal =
    bypassTaxValidation ||
    taxAmount.minus(lineTaxTotal).abs().lte(lineTaxValidationThreshold)
      ? "ok"
      : "error"

  return (
    <SidebarWrapper>
      <InnerWrapper>
        <Header>Invoice information</Header>
        <div>
        <StyledFieldLabel>Arrow ID</StyledFieldLabel>
        <StyledFieldValue> {invoiceInputs && invoiceInputs.invoiceId}</StyledFieldValue>
        </div>
        <InvoiceHeaderNotification />
        {invoiceInputs.validationErrors.length > 0 && (
          <FailedValidationNotifications
            validationFailures={filterDuplicates(
              invoiceInputs.validationErrors,
              (item) => item.validation_rule_error_message,
            ).map((err) => ({
              validationName: err.validation_rule_error_message,
              validationDescription: err.validation_rule_description,
            }))}
          />
        )}
        <UneditableTextbox
          label={"Business Unit"}
          value={invoiceInputs.businessUnit}
          numRows={1}
        />
        {invoiceInputs.hydrated && (
          <>
            <SupplierLookup />

            <Box sx={{ width: "235px" }}>
              <FormControl fullWidth>
                <InputLabel>Invoice type</InputLabel>
                <Select
                  value={invoiceInputs.invoiceType}
                  label="Invoice type"
                  onChange={(e, v) => {
                    handleUpdateStore("invoiceType", e.target.value)
                  }}
                  disabled={LGInternalUser}
                >
                  {Object.values(InvoiceType).map((invoiceType) => (
                    <MenuItem value={invoiceType} key={invoiceType}>
                      {invoiceType[0].toUpperCase() +
                        invoiceType.slice(1).replace(/_/g, " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormTextbox
              label={"Invoice Supplier Name"}
              validationState={"default"}
              value={invoiceInputs.invoiceSupplierName}
              onChange={(e) =>
                handleUpdateStore("invoiceSupplierName", e.target.value)
              }
              disabled={LGInternalUser}
            />
          </>
        )}
        <FormTextbox
          label={"Invoice Number"}
          validationState={invoiceInputs.externalInvoiceId ? "ok" : "error"}
          value={invoiceInputs.externalInvoiceId}
          onChange={(e) =>
            handleUpdateStore("externalInvoiceId", e.target.value)
          }
          disabled={LGInternalUser}
        />
        <FormTextbox
          label={"Currency"}
          validationState={invoiceInputs.currency ? "ok" : "error"}
          value={invoiceInputs.currency}
          onChange={(e) => handleUpdateStore("currency", e.target.value)}
          disabled={LGInternalUser}
        />
        <DependentFields
          labels={["Gross Amount", "Tax Amount", "Total Line Amount"]}
          values={[
            invoiceInputs.grossAmount,
            invoiceInputs.taxAmount,
            invoiceInputs.totalLineAmount,
          ]}
          validationState={totalsValidation}
          onChanges={[
            (v) =>
              validateCurrency(v.target.value) &&
              handleUpdateStore("grossAmount", v.target.value),
            (v) =>
              validateCurrency(v.target.value) &&
              handleUpdateStore("taxAmount", v.target.value),
            (v) =>
              validateCurrency(v.target.value) &&
              handleUpdateStore("totalLineAmount", v.target.value),
          ]}
          disabled={LGInternalUser}
        />
        <FormTextbox
          label={"Line Detail Total"}
          validationState={
            Big(invoiceInputs.purchaseOrderRowSelectionSum).eq(
              safeBig(invoiceInputs.totalLineAmount, 0),
            )
              ? "ok"
              : "error"
          }
          value={invoiceInputs.purchaseOrderRowSelectionSum}
          disabled={true}
        />
        <FormTextbox
          disabled
          label={"Line Tax Total"}
          validationState={validateLineTaxTotal}
          value={lineTaxTotal.toNumber()}
        />
        {showSpotRate && (
          <FormTextbox
            label={"Spot Rate"}
            helperText={`Conversion rate from ${invoiceInputs.currency} to GBP`}
            validationState={
              invoiceInputs.spotRates &&
              validateSpotRatesInput(invoiceInputs.spotRates)
                ? "ok"
                : "error"
            }
            onChange={(e) => {
              dispatch(
                updateInvoiceDetail({
                  key: "spotRates",
                  value: {
                    GBP: e.target.value,
                  },
                }),
              )
            }}
            value={invoiceInputs.spotRates?.GBP || ""}
            disabled={LGInternalUser}
          />
        )}
        <FormTextbox
          label={"Invoice Date"}
          validationState={
            invoiceInputs.invoiceDate &&
            validateFinalDateInput(invoiceInputs.invoiceDate)
              ? "ok"
              : "error"
          }
          value={invoiceInputs.invoiceDate || ""}
          onChange={(e) => {
            handleUpdateStore("invoiceDate", e.target.value)
          }}
          helperText={"DD-MM-YYYY"}
          disabled={LGInternalUser}
        />
        <FormTextbox
          label={"GL Date"}
          validationState={
            invoiceInputs.glDate && validateFinalDateInput(invoiceInputs.glDate)
              ? "ok"
              : "error"
          }
          value={invoiceInputs.glDate}
          onChange={(e) => {
            handleUpdateStore("glDate", e.target.value)
          }}
          helperText={"DD-MM-YYYY"}
          disabled={LGInternalUser}
        />
        <UneditableTextbox
          label={"Payment Terms"}
          value={invoiceInputs.paymentTerms}
          numRows={1}
        />
        <UneditableTextbox
          label={"Payment Method"}
          value={invoiceInputs.paymentMethod}
          numRows={(invoiceInputs.paymentMethod.match(/\n/g) || []).length + 1}
        />
        <UneditableTextbox
          label={"Pay Group"}
          value={invoiceInputs.payGroup}
          numRows={1}
        />
        <BankDetailsLookup />
        {invoiceInputs.opcoSlug === "sunrise" && (
          <>
            <FormTextbox
              label={"QR IBAN"}
              validationState={"default"}
              value={invoiceInputs.qrIban}
              onChange={(e) => handleUpdateStore("qrIban", e.target.value)}
              disabled={LGInternalUser}
            />
            <FormTextbox
              label={"QR IBAN Reference"}
              validationState={"default"}
              value={invoiceInputs.qrIbanReference}
              onChange={(e) =>
                handleUpdateStore("qrIbanReference", e.target.value)
              }
              disabled={LGInternalUser}
            />
          </>
        )}
        <UneditableTextbox
          label={"Bill-To PO"}
          value={invoiceInputs.receiverNamePO}
          numRows={2}
        />
        <MultilineText
          label={"Bill-To Invoice"}
          value={invoiceInputs.receiverNameInvoice}
          lines={2}
          onChange={(e) => {
            handleUpdateStore("receiverNameInvoice", e.target.value)
          }}
          disabled={LGInternalUser}
        />
        <Box sx={{ width: "235px", marginBottom: "16px", position: "relative" }}>
                  <TextboxWrapper>
                    <FormControl fullWidth>
                    <InputLabel>User Flags</InputLabel>
                    <Select
                      value={invoiceInputs.invoice_user_flag || ""}
                      label="User Flags"
                      onChange={(e) => handleUpdateStore("invoice_user_flag", e.target.value)}
                      disabled={LGInternalUser}
                    >
                      {userFlags.map((flag) => (
                        <MenuItem key={flag.key} value={flag.key}>
                          {flag.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl> 
                    {!invoiceInputs.invoice_user_flag && <StyledErrorIcon />}
                  </TextboxWrapper>
                  {!invoiceInputs.invoice_user_flag && (
                    <ValidationMessage>
                      Required field
                    </ValidationMessage>
                  )}
        </Box>
        <MultilineText
          label={"Notes"}
          value={invoiceInputs.note}
          lines={4}
          onChange={(e) => handleUpdateStore("note", e.target.value)}
          disabled={LGInternalUser}
        />
      </InnerWrapper>
    </SidebarWrapper>
  )
}
